/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { dropTask, lastValue } from 'ember-concurrency';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';
import FormValidations from 'cio/utils/form-validations';
import isValidEmail from 'cio/utils/validators/email';
import hasLiquid from 'cio/utils/validators/liquid';
import EmberObject, { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';

import IdentityModel from 'cio/models/identity';

export type FormValues = EmberObject & {
  name: string;
  email: string;
};

interface FromIdentityModalArgs {
  domainName?: string;
  identities?: IdentityModel[];
  identity?: IdentityModel;
  title?: string;
  onClose: () => unknown;
  onSave: (formValues: FormValues) => unknown;
}

const Validations = {
  email(value: string) {
    if (!isValidEmail(value) && !hasLiquid(value)) {
      return 'Please enter a valid email address';
    }

    return true;
  },
};

export default class FromIdentityModal extends Component<FromIdentityModalArgs> {
  @service store!: DS.Store;

  validations: FormValidations;

  @tracked formValues: FormValues;
  @tracked existingIdentityErrorMsg = '';

  get title() {
    return this.args.title ?? 'Edit From Address';
  }

  loadIdentities = dropTask(async () => {
    return await this.store.query('identity', { type: 'email' });
  });

  @lastValue('loadIdentities') identities!: IdentityModel[];

  @computed('identities', 'formValues.{name,email}')
  get identityEmailNameExists() {
    let identities = this.args?.identities ?? this.identities;
    if (identities == null) {
      return false;
    }

    return identities.some((identity) => {
      if (this.args.identity != null && this.args.identity.id === identity.id) {
        return false;
      }

      if (
        (identity.isDeleted as unknown as boolean) === false &&
        identity.name.toLowerCase() === this.formValues.name.toLowerCase() &&
        identity.email === this.formValues.email
      ) {
        this.existingIdentityErrorMsg = isEmpty(identity.name)
          ? `A from address that matches "${identity.email}" already exists`
          : `A from address that matches "${identity.name} <${identity.email}>" already exists`;
        return true;
      }

      return false;
    });
  }

  constructor(owner: unknown, args: FromIdentityModalArgs) {
    super(owner, args);

    this.formValues = this.defaultFormValues();
    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'formValues',
    });
    this.validations.validateModel();

    if (args.identities == null) {
      this.loadIdentities.perform();
    }
  }

  @action
  onChangeEmail(value: string) {
    this.formValues.set('email', value);
    this.validations.validateAttribute('email');
  }

  private defaultFormValues() {
    return EmberObject.create({
      name: this.args.identity?.name ?? '',
      email: this.args.identity?.email ?? '',
    });
  }
}
